import { Link } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";

import { GetSafetyTask } from "../../../services/safetytask";
import { Task } from "../../../types/safetytask";
import { subscribeTenantChange } from "../../../utils/tenantChangeEvent";
import { resolveImageUrl } from "../../../utils";

export default function SafetyTaskSection() {
  const [safetyTaskData, setSafetyTaskData] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSafetyTasks = async () => {
    try {
      setLoading(true);
      const tenantid =
        localStorage.getItem("tenant_id") ||
        sessionStorage.getItem("tenant_id");

      const response = await GetSafetyTask(
        0,
        tenantid ? parseInt(tenantid) : 0,
        0,
        false
      );

      if (response?.Code === 0) {
        setSafetyTaskData(response.safetyTaskDatas || []);
      }
    } catch (err) {
      console.error("Error fetching safety tasks:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch safety tasks. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch saat pertama load
  useEffect(() => {
    fetchSafetyTasks();
  }, []);

  // Fetch ulang saat tenant berubah
  useEffect(() => {
    const handleTenantChange = () => {
      fetchSafetyTasks();
    };

    const unsubscribe = subscribeTenantChange(handleTenantChange);
    return unsubscribe;
  }, []);

  const stats = useMemo(() => {
    return {
      inProgress: safetyTaskData.filter(
        (t) => t.SafetyStatusName === "In Progress"
      ).length,
      done: safetyTaskData.filter((t) => t.SafetyStatusName === "Done").length,
      close: safetyTaskData.filter((t) => t.SafetyStatusName === "Close")
        .length,
      reject: safetyTaskData.filter((t) => t.SafetyStatusName === "Reject")
        .length,
    };
  }, [safetyTaskData]);

  return (
    <div className="relative rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200 overflow-hidden dark:bg-gray-900 dark:ring-gray-700 min-h-44 pb-8">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Safety Task
        </h3>
        <Link
          to="/safetyTask"
          className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-white flex items-center gap-1"
        >
          View all <span>{"\u203A"}</span>
        </Link>
      </div>

      <div className="relative z-10 pr-28">
        {loading ? (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            <div className="flex flex-col items-center text-center gap-1">
              <div className="w-10 h-10 rounded-lg border border-blue-100 bg-white dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight line-clamp-2">
                  In Progress
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-0.5">
                  {stats.inProgress}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center text-center gap-1">
              <div className="w-10 h-10 rounded-lg border border-blue-100 bg-white dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight line-clamp-2">
                  Done
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-0.5">
                  {stats.done}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center text-center gap-1">
              <div className="w-10 h-10 rounded-lg border border-blue-100 bg-white dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <line
                    x1="8"
                    y1="12"
                    x2="16"
                    y2="12"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight line-clamp-2">
                  Close
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-0.5">
                  {stats.close}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center text-center gap-1">
              <div className="w-10 h-10 rounded-lg border border-blue-100 bg-white dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight line-clamp-2">
                  Reject
                </p>
                <p className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-0.5">
                  {stats.reject}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-2 right-2 w-24 h-24 pointer-events-none">
        <img
          src={resolveImageUrl("/images/icon (4).png")}
          alt="Fire safety"
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
}